/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Castiel Zhang
 * Created: 25/09/23
 */

import React from 'react';
import WorkplanStateDisplay from '../contexts/WorkplanContext/WorkplanStateDisplay';
import { Tile } from '../Tile';
import { Flex, Heading } from '@chakra-ui/react';
import { WorkplanSelector } from '@/components/Header/WorkplanSelector';
import { WorkplanActions } from '@/components/Header/WorkplanActions';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import { useWorkplan } from '@/components/contexts/WorkplanContext/useWorkplan';
import { formatName } from '@/wpt-lib/utils/formatName';

type DashboardHeaderTileProps = {
    readonly?: boolean;
    heading?: boolean;
};

export const DashboardHeaderTile: React.FC<DashboardHeaderTileProps> = ({ readonly, heading }) => {
    const { workplanActions } = useNavigateOptions();
    const { supervisor, admin } = useNavigateOptions();

    const { workplan } = useWorkplan();

    return (
        <>
            {heading && (
                <Heading as="h2" size="md">
                    {supervisor || admin
                        ? formatName(workplan?.assignment?.user) + (workplan?.assignment ? "'s" : '')
                        : 'My'}{' '}
                    Workplans
                    <InfoTooltip
                        tooltipLabel={'My Workplans Tooltip'}
                        tooltipInfoText={
                            supervisor || admin
                                ? 'Please use the dropdown arrow to navigate to the specific workplan you wish to review and/or approve'
                                : 'Please use the dropdown arrow to navigate to the specific workplan you wish to view or edit.'
                        }
                    />
                </Heading>
            )}
            <Tile colspan={2} role="landmark">
                <WorkplanStateDisplay
                    render={(workplan) => (
                        <Flex w="100%" alignItems="center" flexWrap="wrap" gap={3}>
                            <WorkplanSelector workplan={workplan} readonly={readonly} />
                            {workplanActions && <WorkplanActions workplan={workplan} />}
                        </Flex>
                    )}
                />
            </Tile>
        </>
    );
};
